import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react'
import type { ChangeEvent, TextareaHTMLAttributes } from 'react'

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  /** Maximum visible rows before the textarea begins scrolling. Default: 8. */
  maxRows?: number
  /** Minimum visible rows. Default: 2. */
  minRows?: number
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  {
    label,
    error,
    id,
    className = '',
    maxRows = 8,
    minRows = 2,
    onChange,
    value,
    required,
    ...rest
  },
  ref,
) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null)
  useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement, [])

  const resize = useCallback(() => {
    const el = innerRef.current
    if (!el) return
    const styles = window.getComputedStyle(el)
    const lineHeight = Number.parseFloat(styles.lineHeight) || 20
    const paddingY = Number.parseFloat(styles.paddingTop) + Number.parseFloat(styles.paddingBottom)
    const borderY =
      Number.parseFloat(styles.borderTopWidth) + Number.parseFloat(styles.borderBottomWidth)
    const minHeight = lineHeight * minRows + paddingY + borderY
    const maxHeight = lineHeight * maxRows + paddingY + borderY

    el.style.height = 'auto'
    const next = Math.min(Math.max(el.scrollHeight + borderY, minHeight), maxHeight)
    el.style.height = `${next}px`
    el.style.overflowY = el.scrollHeight + borderY > maxHeight ? 'auto' : 'hidden'
  }, [maxRows, minRows])

  // Resize on mount and whenever the value prop changes from the outside.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `value` is the trigger we want even though resize() reads it from the DOM, not from the closure.
  useEffect(() => {
    resize()
  }, [resize, value])

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    resize()
    onChange?.(event)
  }

  const baseClasses =
    'block w-full resize-none rounded-md border border-border-strong bg-bg-elevated px-3 py-2 text-sm text-fg placeholder:text-fg-subtle shadow-sm focus:border-fg focus:outline-none focus:ring-1 focus:ring-fg disabled:bg-bg-muted disabled:cursor-not-allowed'
  const errorClasses = error ? 'border-danger focus:border-danger focus:ring-red-500' : ''
  const classes = `${baseClasses} ${errorClasses} ${className}`.trim()

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={id} className="text-sm font-medium text-fg">
          {label}
          {required && (
            <span aria-hidden="true" className="ms-0.5 text-danger">
              *
            </span>
          )}
        </label>
      )}
      <textarea
        ref={innerRef}
        id={id}
        rows={minRows}
        value={value}
        required={required}
        onChange={handleChange}
        className={classes}
        {...rest}
      />
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  )
})
